import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, ImageOff, Loader2, Trash2, Wand2 } from 'lucide-react';
import api, { API, errMsg, fileUrl, TOKEN_KEY } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/context/LanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export default function Gallery() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [preview, setPreview] = useState(null);

  const FILTERS = [
    { id: 'all', label: t('gallery.filterAll') },
    { id: 'landscape', label: '16:9' },
    { id: 'portrait', label: '9:16' },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/generations', {
        params: filter === 'all' ? {} : { orientation: filter },
      });
      setItems(data.generations);
    } catch (e) {
      toast.error(errMsg(e, t('gallery.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/generations/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success(t('gallery.deleted'));
    } catch (e) {
      toast.error(errMsg(e, t('gallery.deleteFailed')));
    }
  };

  const download = async (g) => {
    try {
      const res = await fetch(`${API}/generations/${g.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `spunkram-${g.orientation}-${g.id.slice(0, 8)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(t('gallery.downloadFailed'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{t('gallery.title')}</h1>
          <p className="mt-1 text-sm text-[hsl(var(--text-muted))]">{t('gallery.subtitle')}</p>
        </div>
        <div
          data-testid="history-filter-tabs"
          className="flex items-center gap-1 rounded-[12px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-1"
        >
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`history-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={[
                'rounded-[9px] px-3 py-1.5 text-xs font-semibold transition-colors duration-150',
                filter === f.id ? 'bg-[hsl(var(--brand-red))] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white',
              ].join(' ')}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-red))]" />
        </div>
      ) : items.length === 0 ? (
        <div data-testid="empty-state" className="panel px-6 py-16 text-center">
          <ImageOff className="mx-auto h-9 w-9 text-[hsl(var(--text-muted))]" />
          <p className="mt-4 font-display text-lg font-semibold">{t('gallery.emptyTitle')}</p>
          <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('gallery.emptyDesc')}</p>
          <Button asChild className="mt-6 bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]">
            <Link to="/studyo" data-testid="empty-go-studio">
              <Wand2 className="mr-2 h-4 w-4" /> {t('gallery.goStudio')}
            </Link>
          </Button>
        </div>
      ) : (
        <div data-testid="history-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((g) => (
            <div
              key={g.id}
              data-testid={`history-item-${g.id}`}
              className="panel overflow-hidden transition-colors duration-150 hover:border-[hsl(var(--border-strong))]"
            >
              <button
                type="button"
                onClick={() => g.result_url && setPreview(g)}
                className="block w-full"
                style={{ aspectRatio: g.orientation === 'portrait' ? '9/16' : '16/9' }}
              >
                {g.result_url ? (
                  <img
                    src={fileUrl(g.thumb_url || g.result_url)}
                    alt={t('gallery.coverAlt')}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center bg-[hsl(var(--surface-2))] px-3 text-center">
                    <span className="text-xs text-[hsl(var(--text-muted))]">
                      {g.status === 'failed'
                        ? t('gallery.itemFailed', { error: (g.error || '').slice(0, 60) })
                        : t('gallery.itemGenerating')}
                    </span>
                  </div>
                )}
              </button>

              <div className="space-y-2 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-full bg-[hsl(var(--surface-2))] px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--text-2))]">
                    {g.orientation === 'portrait' ? '9:16' : '16:9'}
                  </span>
                  <span className="font-mono-alt text-[10px] text-[hsl(var(--text-muted))]">{g.model_label}</span>
                </div>
                <p className="line-clamp-2 min-h-[32px] text-xs leading-relaxed text-[hsl(var(--text-muted))]">
                  {g.prompt || t('gallery.noPrompt')}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={!g.result_url}
                    onClick={() => download(g)}
                    data-testid={`gallery-download-${g.id}`}
                    className="h-8 flex-1 bg-[hsl(var(--brand-red))] text-xs font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" /> {t('common.download')}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`gallery-delete-${g.id}`}
                        className="h-8 w-8 border border-[hsl(var(--border-soft))] text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--brand-red))]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))]">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('gallery.deleteConfirmTitle')}</AlertDialogTitle>
                        <AlertDialogDescription className="text-[hsl(var(--text-muted))]">
                          {t('common.irreversible')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]">
                          {t('common.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove(g.id)}
                          data-testid={`gallery-delete-confirm-${g.id}`}
                          className="bg-[hsl(var(--brand-red))] text-white hover:bg-[hsl(var(--brand-red-2))]"
                        >
                          {t('common.delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-3">
          {preview && (
            <img
              src={fileUrl(preview.result_url)}
              alt={t('gallery.previewAlt')}
              data-testid="gallery-preview-image"
              className="max-h-[78vh] w-full rounded-[10px] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
